// PR-AK — Admin bulk-cancel mutation hook.
//
// Loops the shared single-cancel helper, then delegates email aggregation
// to the `send-bulk-cancellation-email` edge fn (Q5: 1 mail per recipient
// listing all their cancelled bookings).
//
// Returns per-booking results so the dialog can render a partial-failure
// detail-list with retry button (Q4 Option A).
//
// Hotfix history:
//   - Initial PR-AK shipped a client-side aggregation that selected
//     `profiles.email` from public.profiles. That column doesn't exist
//     (email lives in auth.users), so the supabase select threw and the
//     dialog catch swallowed the error → bulk-cancel button silently
//     failed in prod. Email lookup is now fully server-side via
//     send-bulk-cancellation-email which uses
//     supabaseAdmin.auth.admin.getUserById.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { cancelSingleBooking, type CancelBookingResult } from "@/lib/cancelBookingHelper";

export interface BulkCancelOpts {
  bookingIds: string[];
  refund: boolean;
  reason: string;
  triggerWaitlistOffer: boolean;
  chargeLateCancelFee: boolean;
}

export interface BulkCancelMutationResult {
  results: CancelBookingResult[];
  emailsSent: number;
  emailDispatchError?: string;
}

export function useAdminBulkCancel() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const qc = useQueryClient();

  return useMutation<BulkCancelMutationResult, Error, BulkCancelOpts>({
    mutationFn: async (opts: BulkCancelOpts) => {
      if (!user || !organization) {
        throw new Error("Inte inloggad");
      }
      if (opts.bookingIds.length === 0) {
        return { results: [], emailsSent: 0 };
      }

      // Fetch booking + payment info (no email — server fn handles that).
      const { data: bookings, error: bErr } = await supabase
        .from("bookings")
        .select("id, user_id, court_id, start_time, end_time, standard_price_total, courts(name, hall_id), profiles:bookings_user_id_profiles_fkey(full_name)")
        .in("id", opts.bookingIds);
      if (bErr) throw bErr;

      const { data: allPayments } = await supabase
        .from("booking_payments")
        .select("*")
        .in("booking_id", opts.bookingIds);

      const results: CancelBookingResult[] = [];

      // Sequential loop — keeps refund/Stripe rate friendly + matches the
      // single-cancel atomicity model. No artificial throttle (Q1 confirmed
      // no MATCHi push-spam risk).
      for (const bookingId of opts.bookingIds) {
        const booking = bookings?.find((b) => b.id === bookingId);
        if (!booking) {
          results.push({ bookingId, success: false, error: "Bokningen hittades inte" });
          continue;
        }
        const payments = allPayments?.filter((p) => p.booking_id === bookingId) ?? [];

        const result = await cancelSingleBooking(
          {
            bookingId,
            payments,
            booking: {
              id: booking.id,
              user_id: booking.user_id,
              court_id: booking.court_id,
              start_time: booking.start_time,
              end_time: booking.end_time,
              standard_price_total: booking.standard_price_total,
              courts: booking.courts as any,
              profiles: booking.profiles as any,
            },
          },
          {
            refund: opts.refund,
            reason: opts.reason,
            triggerWaitlistOffer: opts.triggerWaitlistOffer,
            chargeLateCancelFee: opts.chargeLateCancelFee,
            adminUserId: user.id,
            organizationId: organization.id,
          },
        );
        results.push(result);
      }

      // ── Email aggregation per recipient (Q5 Option B) ────────────────
      // Server-side via send-bulk-cancellation-email edge fn:
      //   - Resolves each booking owner's email via auth.users (admin API)
      //   - Groups by user_id, sends 1 mail per unique recipient
      //   - Single recipient with 1 booking → standard booking_cancellation
      //   - Single recipient with N bookings → booking_cancellation_bulk
      // Email dispatch failures are non-fatal — the bookings are still
      // cancelled. Surface a soft warning via the result.
      const successfulIds = results.filter((r) => r.success).map((r) => r.bookingId);
      let emailsSent = 0;
      let emailDispatchError: string | undefined;

      if (successfulIds.length > 0) {
        try {
          const { data, error } = await supabase.functions.invoke(
            "send-bulk-cancellation-email",
            { body: { bookingIds: successfulIds } },
          );
          if (error) {
            console.error("[useAdminBulkCancel] email dispatch error:", error);
            emailDispatchError = error.message ?? String(error);
          } else if (data && typeof data.emailsSent === "number") {
            emailsSent = data.emailsSent;
          }
        } catch (err) {
          console.error("[useAdminBulkCancel] email dispatch threw:", err);
          emailDispatchError = err instanceof Error ? err.message : String(err);
        }
      }

      return { results, emailsSent, emailDispatchError };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schedule-bookings"] });
      qc.invalidateQueries({ queryKey: ["admin-all-bookings"] });
      qc.invalidateQueries({ queryKey: ["admin-booking-payments"] });
      qc.invalidateQueries({ queryKey: ["booking-detail"] });
      qc.invalidateQueries({ queryKey: ["booking-payments-detail"] });
      qc.invalidateQueries({ queryKey: ["booking-activity-log"] });
    },
  });
}
